import React from 'react';

export interface RevenueChartData {
  month: string;
  revenue: number;
}

export interface RevenueChartProps {
  data: RevenueChartData[];
  isLoading?: boolean;
  height?: number;
}

const RevenueChart: React.FC<RevenueChartProps> = ({ data, isLoading, height = 300 }) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h2>
        <div className="h-[300px] flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading chart data...</div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h2>
        <div className="h-[300px] flex items-center justify-center text-gray-500">
          No revenue data available
        </div>
      </div>
    );
  }

  const maxRevenue = Math.max(...data.map(d => d.revenue), 0);
  const minRevenue = Math.min(...data.map(d => d.revenue), 0);
  const range = maxRevenue - minRevenue || 1;
  const chartHeight = height - 80;
  const chartWidth = 1000;
  const barWidth = chartWidth / data.length - 10;
  const padding = 40;

  const points = data.map((item, index) => {
    const x = (index * (chartWidth / data.length)) + (chartWidth / data.length / 2);
    const y = chartHeight - ((item.revenue - minRevenue) / range) * chartHeight;
    return { x, y, revenue: item.revenue, month: item.month };
  });

  const pathData = points.map((point, index) => 
    `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
  ).join(' ');

  const areaPathData = `${pathData} L ${points[points.length - 1].x} ${chartHeight} L ${points[0].x} ${chartHeight} Z`;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h2>
      <div className="relative" style={{ height: `${height}px` }}>
        <svg width="100%" height={height} viewBox={`0 0 ${chartWidth} ${height}`} className="overflow-visible">
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = padding + (chartHeight * ratio);
            const value = maxRevenue - (range * ratio);
            return (
              <g key={ratio}>
                <line
                  x1={padding}
                  y1={y}
                  x2={chartWidth - padding}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                  strokeDasharray="4 4"
                />
                <text
                  x={padding - 10}
                  y={y + 4}
                  textAnchor="end"
                  className="text-xs fill-gray-500"
                >
                  €{value.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </text>
              </g>
            );
          })}

          {/* Area fill */}
          <path
            d={areaPathData}
            fill="url(#revenueGradient)"
            opacity="0.3"
          />

          {/* Line */}
          <path
            d={pathData}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Points */}
          {points.map((point, index) => (
            <g key={index}>
              <circle
                cx={point.x}
                cy={point.y}
                r="5"
                fill="#3b82f6"
                className="hover:r-7 transition-all cursor-pointer"
              >
                <title>{point.month}: €{point.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</title>
              </circle>
            </g>
          ))}

          {/* X-axis labels */}
          {data.map((item, index) => {
            const x = (index * (chartWidth / data.length)) + (chartWidth / data.length / 2);
            return (
              <text
                key={index}
                x={x}
                y={chartHeight + padding - 10}
                textAnchor="middle"
                className="text-xs fill-gray-600"
              >
                {item.month}
              </text>
            );
          })}

          <defs>
            <linearGradient id="revenueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
};

export default RevenueChart;

